import { CopyUserComponent } from '@admin/components/dialogs/copy-user/copy-user.component';
import { AdminService } from '@admin/services/admin.service';
import { DataSource } from '@angular/cdk/collections';
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { CbreAlertService } from '@cbre/alerts';
import { AddLocationComponent } from '@shared/dialogs/add-location/add-location.component';
import { isNotNull } from '@shared/functions';
import { DataObject } from '@shared/models/data-object.model';
import { IUserSettings } from '@shared/models/iuser-settings.model';
import { ILocationPermission, IUser } from '@shared/models/iuser.model';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';
import { DialogService } from '@shared/services/dialog.service';
import { UserService } from '@user/services/user.service';
import { L10nTranslationService } from 'angular-l10n';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

@Component({
  selector: 'miq-location-permissions',
  templateUrl: './location-permissions.component.html',
  styleUrls: ['./location-permissions.component.scss'],
  standalone: false,
})
export class LocationPermissionsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public locationPermissions: ILocationPermission[] = [];
  @Input() public requestMode = false;
  @Input() pageType: string;
  @Input() userId: string;
  @Input() isAddUser: boolean;
  @Input() isPendingUser: boolean;

  @Output() copyResultEvent = new EventEmitter<any>();
  @Output()
  public formReady: EventEmitter<UntypedFormControl>;
  public locationsFormControl: UntypedFormControl;
  public userInfo: IUser;
  public dataSubject: BehaviorSubject<ILocationPermission[]>;
  public locationPermissionsDataSource: DataSource<ILocationPermission>;
  public recordsArray: ILocationPermission[] = [];
  public userSettings: IUserSettings;

  public displayedColumns: string[] = [
    'globalRegion',
    'division',
    'market',
    'submarket',
    'productType',
    'businessJustification',
    'confidentiality',
    'status',
    'buttons',
  ];

  constructor(
    private notificationService: DialogService,
    private httpService: AdminService,
    private userService: UserService,
    private resolver: UserInfoResolver,
    private cbreAlertService: CbreAlertService,
    private formBuilder: UntypedFormBuilder,
    public translationService: L10nTranslationService,
    @Inject(CbreLoggerToken) private logger: CbreLogger
  ) {
    this.formReady = new EventEmitter();
    this.locationsFormControl = this.formBuilder.control(this.locationPermissions, [
      Validators.required,
      Validators.minLength(1),
    ]);
    this.userInfo = this.resolver.resolve();
    this.dataSubject = new BehaviorSubject([]);
    this.locationPermissionsDataSource = new LocationPermissionsDataSource(this.dataSubject);
  }

  ngOnInit() {
    this.formReady.emit(this.locationsFormControl);
    if (this.locationPermissions) {
      this.dataSubject.next(this.locationPermissions);
    } else {
      this.locationPermissions = [];
    }

    if (!this.isAddUser && this.pageType === 'Admin') {
      this.displayedColumns.splice(4, 0, 'businessJustification');
    }
    if (this.pageType === 'Admin' && this.isPendingUser) {
      this.displayedColumns.splice(5, 0, 'Approve/Reject');
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.locationPermissions && !!changes.locationPermissions.currentValue) {
      this.locationPermissions = changes.locationPermissions.currentValue;
      this.locationsFormControl.patchValue(this.locationPermissions);
      this.dataSubject.next(this.locationPermissions);
    }
    this.assignRowID(this.locationPermissions);
  }

  public openDialog() {
    const data = { pageType: this.pageType };
    this.notificationService
      .openDialog(AddLocationComponent, data, { disableClose: true, panelClass: 'table-modals' })
      .pipe(filter(result => !!result))
      .subscribe(result => {
        const newPermissionsArray = [];
        result.forEach(record => {
          if (!this.findPermissionExists(this.locationPermissions, record)) {
            this.locationPermissions.push(record);
            newPermissionsArray.push(record);
            this.locationsFormControl.setValue(this.locationPermissions);
          }
        });
        if (this.pageType && this.pageType === 'user' && !this.requestMode && newPermissionsArray.length > 0) {
          this.httpService.addPermissions(this.userId, newPermissionsArray).subscribe(
            () => {
              this.userService
                .getUserPermissions(this.userId)
                .pipe(take(1))
                .subscribe(userPermissions => {
                  this.locationPermissions = userPermissions;
                  this.dataSubject.next(this.locationPermissions);
                });
            },
            err => {
              this.logger.debug(`${err}`);
            }
          );
        } else {
          this.assignRowID(this.locationPermissions);
          this.dataSubject.next(this.locationPermissions);
        }
      });
  }

  /*
   * Delete user permissions
   */
  public deletePermission(userId: number, permissionsID: number, rowId: number) {
    if (permissionsID && this.pageType && this.pageType === 'user' && !this.requestMode) {
      this.httpService
        .deletePermissions(userId, permissionsID)
        .pipe(take(1))
        .subscribe(_result => {
          this.userService
            .getUserPermissions(userId)
            .pipe(take(1))
            .subscribe(response => {
              this.locationPermissions = response;
              this.dataSubject.next(this.locationPermissions);
            });
        });
    } else {
      this.locationPermissions = this.locationPermissions.filter(permission => permission.rowId !== rowId);
      this.locationsFormControl.setValue(this.locationPermissions);
      this.formReady.emit(this.locationsFormControl);
      this.dataSubject.next(this.locationPermissions);
    }
  }

  /*
   * edit user permissions
   */
  public edit(locationPermission: ILocationPermission) {
    const data = { locationPermission, pageType: this.pageType };
    this.notificationService
      .openDialog(AddLocationComponent, data, { disableClose: true, panelClass: 'table-modals' })
      .subscribe(result => {
        if (result) {
          result.map(record => {
            const permission = this.findPermissionExists(this.locationPermissions, record);
            if (!permission) {
              this.locationPermissions.splice(locationPermission.rowId, 1);
              return this.locationPermissions.push(record);
            }
            this.locationPermissions = this.locationPermissions.map(locPerm =>
              locPerm.id === record.id ? { ...locPerm, ...record } : locPerm
            );
          });
          this.dataSubject.next(this.locationPermissions);
        }
        this.assignRowID(this.locationPermissions);
      });
  }

  /**
   * check for duplicate records
   */
  public findPermissionExists(locationPermission: ILocationPermission[], record: ILocationPermission): any {
    if (locationPermission && locationPermission.length > 0) {
      for (const object of locationPermission) {
        if (
          object.globalRegionId === record.globalRegionId &&
          object.divisionId === record.divisionId &&
          object.marketId === record.marketId &&
          object.subMarketId === record.subMarketId &&
          object.districtId === record.districtId &&
          object.neighborhoodId === record.neighborhoodId &&
          object.productTypeId === record.productTypeId
        ) {
          return object;
        }
      }
    }
    return null;
  }

  /**
   * assign row Id's to manage temporary state in UI for location permissions
   */
  public assignRowID(locationPermission: ILocationPermission[]): void {
    if (locationPermission && locationPermission.length > 0) {
      locationPermission.forEach((record, index) => {
        record.rowId = index;
      });
    }
  }

  /**
   * Clone location permission from existing user
   */
  public cloneUser(): void {
    const options = {};
    this.notificationService.openDialog(CopyUserComponent, null, options).subscribe(result => {
      if (!!result) {
        const cloneType = this._getCloneType(result);
        this.httpService
          .cloneUser(result.email, cloneType)
          .pipe(take(1))
          .subscribe(
            response => {
              if (response && response.permissions && response.permissions.length > 0) {
                this.copyResultEvent.emit(response);
                response.permissions.forEach(record => {
                  record.updateBy = this.userInfo.emailAddress;
                  record.createBy = this.userInfo.emailAddress;
                  if (!this.findPermissionExists(this.locationPermissions, record)) {
                    this.locationPermissions.push(record);
                  }
                });
                if (response.settings) {
                  this.userSettings = response.settings;
                }
              }
              this.dataSubject.next(this.locationPermissions);
              this.cbreAlertService.showSuccessSnackBar_l10n('shared.permissionsAdded');
            },
            // Show error message
            err => this.cbreAlertService.showErrorSnackBar(err.message)
          );
      }
    });
  }

  private _getCloneType(results: { profileSettings: DataObject; rolePermissions: DataObject }) {
    const { profileSettings, rolePermissions } = results;
    if ([profileSettings, rolePermissions].every(isNotNull)) {
      return 'both';
    } else if (isNotNull(profileSettings)) {
      return 'settings';
    } else if (isNotNull(rolePermissions)) {
      return 'permissions';
    }
    return null;
  }

  ngOnDestroy() {
    /** label translation purpose added */
  }
}

class LocationPermissionsDataSource implements DataSource<any> {
  constructor(private dataSubject: BehaviorSubject<any>) {}
  connect(): Observable<any> {
    return this.dataSubject.asObservable();
  }
  disconnect() {
    // This is intentional
  }
}
